/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChannelReference } from '../models/ChannelReference';
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { SliderState } from '../models/SliderState';
import type { StrokeSettings } from '../models/StrokeSettings';
import type { StrokeSettingsRsp } from '../models/StrokeSettingsRsp';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class SliderService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get the current state of the device slider.
     * Get the current state of the device slider.
     *
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Slider state.
     * @throws ApiError
     */
    public getSlideState(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: SliderState;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/slider/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Set the stroke settings of the device slider.
     * Set the minimum and/or maximum allowed relative slider position of the device.
     * The slider will not move outside the specified stroke zone.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody Stroke settings
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device response. In case of a device error, the response will contain an ErrorResponse object.
     * @throws ApiError
     */
    public setStroke(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: StrokeSettings,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: StrokeSettingsRsp;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/slider/stroke',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get the stroke settings of the device slider.
     * Get the minimum and maximum allowed relative slider position of the device.
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device response. In case of a device error, the response will contain an ErrorResponse object.
     * @throws ApiError
     */
    public getStroke(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: StrokeSettingsRsp;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/slider/stroke',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            errors: {
                400: `Bad request.`,
            },
        });
    }

}
